import { ReactElement, useMemo } from 'react'

import type { ReviewEntryQuestion } from '../../../../types/reviewEntryQuestion'
import { ReviewEntryClarificationCard } from './elements/ReviewEntryClarificationCard'
import * as Styled from './styles'
import { getClarificationQuestionKey } from './utils/getClarificationQuestionKey'
import { getClarificationQuestions } from './utils/getClarificationQuestions'

interface ReviewEntryAiSuggestionsProps {
  dismissedQuestionKeys: string[]
  entryId: string
  onDismissQuestion: (questionKey: string) => void
  questions: ReviewEntryQuestion[]
}

const MAX_VISIBLE_QUESTIONS = 2

export const ReviewEntryAiSuggestions = ({
  dismissedQuestionKeys,
  entryId,
  onDismissQuestion,
  questions,
}: ReviewEntryAiSuggestionsProps): ReactElement | null => {
  const clarificationQuestions = useMemo(() => getClarificationQuestions(questions), [questions])

  const visibleQuestions = useMemo(
    () =>
      clarificationQuestions
        .map((question, index) => ({
          question,
          questionKey: getClarificationQuestionKey(entryId, question, index),
        }))
        .filter(({ questionKey }) => !dismissedQuestionKeys.includes(questionKey))
        .slice(0, MAX_VISIBLE_QUESTIONS),
    [clarificationQuestions, dismissedQuestionKeys, entryId],
  )

  if (!visibleQuestions.length) {
    return null
  }

  return (
    <Styled.ReviewEntryAiSuggestionsWrapper data-testid="review-entry-ai-suggestions">
      {visibleQuestions.map(({ question, questionKey }) => (
        <ReviewEntryClarificationCard
          key={questionKey}
          onDismiss={() => onDismissQuestion(questionKey)}
          question={question}
        />
      ))}
    </Styled.ReviewEntryAiSuggestionsWrapper>
  )
}
